import React from 'react';
import { Card } from './ui/card';
import { Heart, Plane, UtensilsCrossed, Trophy } from 'lucide-react';

const WhoIAm = () => {
  return (
    <section id="personal" className="pt-24 md:pt-24 pb-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="mb-16 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Beyond the LinkedIn Bio</h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Before I tell you about campaigns and budgets, let me tell you who I am when I am not 
            optimizing conversion funnels.
          </p>
        </div>

        {/* Photo Gallery - Mobile Responsive */}
        <div className="mb-16">
          {/* Desktop Layout: 4 columns */}
          <div className="hidden md:grid md:grid-cols-4 gap-4">
            <div className="col-span-2 rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/i8kcaz64_1FA7B2EB-1658-4C4D-AAB9-5EAD6E45E226.JPG"
                alt="Anshul at industry event"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/us1tlktp_D21F7DB6-ED65-442F-9CC7-783A9A5EB52A.JPG"
                alt="Anshul Kumar"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/1xy4nnhs_FB_IMG_1576323117814.jpg"
                alt="Anshul Kumar"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/fyww0e65_915A6888-680A-46FE-9632-B4857EF3E036.JPG"
                alt="Anshul at cricket"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/mtwqt9ei_517E6C69-710B-4003-AD08-31D4D8388F5C.JPG"
                alt="Anshul Kumar"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="col-span-2 rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/9oscxkd2_C3DB6E82-5963-4D54-9F85-BE57CDA9930A.JPG"
                alt="Anshul traveling"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>

          {/* Mobile Layout: Single column with consistent aspect ratio */}
          <div className="md:hidden flex flex-col gap-4">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/i8kcaz64_1FA7B2EB-1658-4C4D-AAB9-5EAD6E45E226.JPG"
                alt="Anshul at industry event"
                className="w-full h-72 object-cover object-top"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/us1tlktp_D21F7DB6-ED65-442F-9CC7-783A9A5EB52A.JPG"
                alt="Anshul Kumar"
                className="w-full h-80 object-cover object-top"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/1xy4nnhs_FB_IMG_1576323117814.jpg"
                alt="Anshul Kumar"
                className="w-full h-80 object-cover object-center"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/fyww0e65_915A6888-680A-46FE-9632-B4857EF3E036.JPG"
                alt="Anshul at cricket"
                className="w-full h-72 object-cover object-top"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/mtwqt9ei_517E6C69-710B-4003-AD08-31D4D8388F5C.JPG"
                alt="Anshul Kumar"
                className="w-full h-72 object-cover object-top"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/9oscxkd2_C3DB6E82-5963-4D54-9F85-BE57CDA9930A.JPG"
                alt="Anshul traveling"
                className="w-full h-72 object-cover object-center"
              />
            </div>
          </div>
        </div>

        {/* Story */}
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6 text-lg text-slate-700 leading-relaxed">
            <p>
              I believe the best ideas come not from boardrooms, but from conversations over chai, from watching 
              how people behave in streets, from understanding what makes cultures tick. That is why I have spent 
              years traveling across continents—not as a tourist checking boxes, but as someone genuinely curious 
              about how people live, think, and decide.
            </p>

            <p>
              From the outdoor sophistication of Sydney to the engineering precision of Germany. From the Alpine 
              craftsmanship of Switzerland to the passionate creativity of Italy. From the elegant minimalism of 
              France to the progressive innovation of Netherlands. Each country taught me something different about 
              markets, people, and what drives behavior. Ten countries. Countless meals. Endless conversations. 
              Every experience shaped how I see marketing today.
            </p>

            <p>
              Cricket is not just a sport for me—it is a masterclass in strategy, teamwork, and reading the game. 
              I see marketing the same way. Sometimes you play the long game, building slowly. Sometimes you go 
              aggressive. Always, you adapt to what the situation demands.
            </p>

            <p>
              Food is another obsession. From street vendors in Bangkok to Michelin-starred experiments in Paris, 
              from hole-in-the-wall places in Delhi to family trattorias in Rome—I believe the best stories are 
              told over meals. Understanding food culture has taught me more about consumer behavior than most 
              marketing textbooks ever could.
            </p>

            <p className="text-slate-900 font-semibold text-xl pt-4">
              This is why I do not believe in marketing formulas. Real marketing is about understanding humans. 
              And you only understand humans when you live among them, travel with them, eat with them, and stay 
              endlessly curious about what makes them tick. That curiosity is what separates marketing that works 
              from marketing that is just noise.
            </p>
          </div>

          {/* Passions */}
          <div className="mt-12 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <Trophy className="text-teal-600" size={24} />
              <div>
                <div className="font-semibold text-slate-900">Sports & Cricket</div>
                <div className="text-sm text-slate-600">Strategy in motion</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <Plane className="text-teal-600" size={24} />
              <div>
                <div className="font-semibold text-slate-900">Global Explorer</div>
                <div className="text-sm text-slate-600">10+ countries, countless stories</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <UtensilsCrossed className="text-teal-600" size={24} />
              <div>
                <div className="font-semibold text-slate-900">Food & Culture</div>
                <div className="text-sm text-slate-600">From street food to fine dining</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <Heart className="text-teal-600" size={24} />
              <div>
                <div className="font-semibold text-slate-900">Human Stories</div>
                <div className="text-sm text-slate-600">Endlessly curious</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoIAm;